import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Row, Col, Button, Spin, Divider, message, Input, Select } from 'antd';
import { 
  ArrowLeftOutlined, 
  InfoCircleOutlined, 
  DashboardOutlined, 
  ReloadOutlined, 
  PoweroffOutlined, 
  EditOutlined, 
  CloudUploadOutlined, 
  SettingOutlined
} from '@ant-design/icons';
import axios from 'axios';
import EditBoxModal from '../EditBoxModal';

const { Option } = Select;

const MobileScreenDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [box, setBox] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const [isNameModalVisible, setIsNameModalVisible] = useState(false);
  const [isSettingsModalVisible, setIsSettingsModalVisible] = useState(false);
  const [updating, setUpdating] = useState(false);
  
  const [editData, setEditData] = useState({ 
    box_name: '', 
    location: '',
    resolution: '1920x1080',
    rotation: '0'
  });

  // Popravljeno: useCallback sprečava nepotrebno osvežavanje i rešava Line 70 warning
  const fetchBoxDetails = useCallback(async () => {
    try {
      const response = await axios.get('/api/box/list/');
      const selectedBox = response.data.find(b => b.id.toString() === id);
      
      if (selectedBox) {
        setBox(selectedBox);
        if (!isNameModalVisible && !isSettingsModalVisible) {
          setEditData({ 
            box_name: selectedBox.box_name || '', 
            location: selectedBox.location || '',
            resolution: selectedBox.resolution || '1920x1080',
            rotation: selectedBox.rotation || '0'
          });
        }
      }
      setLoading(false);
    } catch (error) {
      console.error('Error loading box details:', error);
      setLoading(false);
    }
  }, [id, isNameModalVisible, isSettingsModalVisible]);

  useEffect(() => {
    fetchBoxDetails();
    const interval = setInterval(() => {
      if (!isNameModalVisible && !isSettingsModalVisible) fetchBoxDetails();
    }, 10000);
    return () => clearInterval(interval);
  }, [fetchBoxDetails, isNameModalVisible, isSettingsModalVisible]);

  const handleSave = async () => {
    try {
      setUpdating(true);
      message.loading({ content: 'Saving...', key: 'save' });
      await axios.post('/api/box/rename-box/', { box_id: box.box_id, box_name: editData.box_name, location: editData.location });
      await axios.post('/api/box/update-settings/', { box_id: box.box_id, resolution: editData.resolution, rotation: editData.rotation });
      message.success({ content: 'Saved!', key: 'save' });
      setIsNameModalVisible(false);
      setIsSettingsModalVisible(false);
      fetchBoxDetails(); 
    } catch (error) { message.error({ content: 'Error', key: 'save' }); } finally { setUpdating(false); }
  };

  const handleAction = async (actionType) => {
    try {
      message.loading({ content: 'Sending...', key: 'action' });
      await axios.post(`/api/box/control/`, { box_id: box.box_id, action: actionType });
      message.success({ content: 'Sent!', key: 'action' });
    } catch (error) { message.error({ content: 'Error', key: 'action' }); }
  };

  const getTempColor = (t) => parseFloat(t) > 75 ? '#ef4444' : '#34d399';
  const getLoadColor = (l) => parseFloat(l) > 80 ? '#ef4444' : '#34d399';
  const getRamColor = (r) => (r && parseInt(r.split('/')[0]) > 3200) ? '#ef4444' : '#34d399';
  const getDiskColor = (d) => (d && parseInt(d.split('/')[0]) > 22) ? '#ef4444' : '#34d399';

  if (loading) return <div style={{ padding: '100px', textAlign: 'center', background: '#0f172a', minHeight: '100vh' }}><Spin size="large" /></div>;
  if (!box) return <div style={{ color: 'white', padding: '50px', background: '#0f172a', minHeight: '100vh' }}>Not found.</div>;

  return (
    <div style={{ background: '#0f172a', minHeight: '100vh', padding: '12px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/')} style={{ background: '#1e293b', color: '#94a3b8', border: '1px solid #334155' }} />
        <h3 style={{ color: 'white', margin: 0 }}>{box.box_name || box.box_id}</h3>
      </div>

      <Card title={<span style={{ color: 'white' }}>Quick Actions</span>} style={{ background: '#1e293b', border: '1px solid #334155', marginBottom: '16px' }}>
        <Row gutter={[8, 8]}>
          <Col span={12}><Button block icon={<ReloadOutlined />} onClick={() => handleAction('restart_player')} style={{ background: '#0f172a', color: '#60a5fa', border: '1px solid #334155' }}>Player</Button></Col>
          <Col span={12}><Button block icon={<PoweroffOutlined />} onClick={() => handleAction('restart_box')} style={{ background: '#0f172a', color: '#f87171', border: '1px solid #334155' }}>Reboot</Button></Col>
          <Col span={24}><Button block icon={<CloudUploadOutlined />} onClick={() => handleAction('update_apk')} style={{ background: '#0f172a', color: '#34d399', border: '1px solid #334155' }}>Update APK</Button></Col>
        </Row>
      </Card>

      <Card title={<span style={{ color: 'white' }}><InfoCircleOutlined /> Info & Network</span>} style={{ background: '#1e293b', border: '1px solid #334155', marginBottom: '16px' }}>
        <div className="m-item" onClick={() => setIsNameModalVisible(true)}><span className="m-label">NAME</span><span className="m-val" style={{ color: '#60a5fa' }}>{box.box_name || '-'} <EditOutlined /></span></div>
        <Divider className="m-div" />
        <div className="m-item"><span className="m-label">BOX ID</span><span className="m-val">{box.box_id}</span></div>
        <Divider className="m-div" />
        <div className="m-item"><span className="m-label">LOCATION</span><span className="m-val">{box.location || '-'}</span></div>
        <Divider className="m-div" />
        <div className="m-item"><span className="m-label">IP ADDRESS</span><span className="m-val">{box.ip_address || '-'}</span></div>
        <Divider className="m-div" />
        <div className="m-item"><span className="m-label">UPTIME</span><span className="m-val">{box.uptime || '-'}</span></div>
        <Divider className="m-div" />
        <div className="m-item" style={{ flexDirection: 'column', alignItems: 'flex-start' }}><span className="m-label">LAST SEEN</span><span className="m-val" style={{ color: '#94a3b8' }}>{new Date(box.last_seen).toLocaleString('sr-RS')}</span></div>
      </Card>

      <Card title={<span style={{ color: 'white' }}><DashboardOutlined /> System</span>} style={{ background: '#1e293b', border: '1px solid #334155', marginBottom: '16px' }}>
        <div className="m-item"><span className="m-label">STATUS</span><span style={{ color: box.status === 'online' ? '#34d399' : '#ef4444', fontWeight: 'bold' }}>{box.status.toUpperCase()}</span></div>
        <Divider className="m-div" />
        <div className="m-item"><span className="m-label">CPU TEMP</span><span className="m-val" style={{ color: getTempColor(box.cpu_temp) }}>{box.cpu_temp || '-'}</span></div>
        <Divider className="m-div" />
        <div className="m-item"><span className="m-label">CPU LOAD</span><span className="m-val" style={{ color: getLoadColor(box.cpu_load) }}>{box.cpu_load || '-'}%</span></div>
        <Divider className="m-div" />
        <div className="m-item"><span className="m-label">RAM</span><span className="m-val" style={{ color: getRamColor(box.ram_usage) }}>{box.ram_usage || '-'}</span></div>
        <Divider className="m-div" />
        <div className="m-item"><span className="m-label">DISK</span><span className="m-val" style={{ color: getDiskColor(box.disk_usage) }}>{box.disk_usage || '-'}</span></div>
      </Card>

      <Card title={<span style={{ color: 'white' }}><SettingOutlined /> Screen & Software</span>} style={{ background: '#1e293b', border: '1px solid #334155', marginBottom: '16px' }}>
        <div className="m-item"><span className="m-label">PLAYLIST</span><span className="m-val" style={{ color: '#facc15' }}>{box.current_playlist || 'No Playlist'}</span></div>
        <Divider className="m-div" />
        <div className="m-item" onClick={() => setIsSettingsModalVisible(true)}><span className="m-label">RESOLUTION</span><span className="m-val">{box.resolution || '-'} <EditOutlined /></span></div>
        <Divider className="m-div" />
        <div className="m-item"><span className="m-label">ROTATION</span><span className="m-val">{box.rotation === '1' ? '90°' : box.rotation === '2' ? '180°' : box.rotation === '3' ? '270°' : '0°'}</span></div>
        <Divider className="m-div" />
        <div className="m-item"><span className="m-label">SERVICE APK</span><span className="m-val">{box.service_apk_version || '-'}</span></div>
        <Divider className="m-div" />
        <div className="m-item"><span className="m-label">PLAYER APK</span><span className="m-val">{box.player_apk_version || '-'}</span></div>
      </Card>

      <EditBoxModal isVisible={isNameModalVisible} onOk={handleSave} onCancel={() => setIsNameModalVisible(false)} updating={updating} title="Name & Location">
        <label className="m-label">NAME</label><Input value={editData.box_name} onChange={(e) => setEditData({...editData, box_name: e.target.value})} className="m-input" style={{ marginBottom: 10 }} />
        <label className="m-label">LOCATION</label><Input value={editData.location} onChange={(e) => setEditData({...editData, location: e.target.value})} className="m-input" />
      </EditBoxModal>

      <EditBoxModal isVisible={isSettingsModalVisible} onOk={handleSave} onCancel={() => setIsSettingsModalVisible(false)} updating={updating} title="Settings">
        <label className="m-label">RESOLUTION</label><Select value={editData.resolution} onChange={(v) => setEditData({...editData, resolution: v})} style={{ width: '100%', marginBottom: 10 }} dropdownStyle={{ background: '#1e293b' }}><Option value="1920x1080">1920x1080</Option><Option value="1280x720">1280x720</Option></Select>
        <label className="m-label">ROTATION</label><Select value={editData.rotation} onChange={(v) => setEditData({...editData, rotation: v})} style={{ width: '100%' }} dropdownStyle={{ background: '#1e293b' }}><Option value="0">0°</Option><Option value="1">90°</Option><Option value="2">180°</Option><Option value="3">270°</Option></Select>
      </EditBoxModal>

      <style>{`
        .m-item { display: flex; justify-content: space-between; align-items: center; padding: 4px 0; }
        .m-label { color: #64748b; font-size: 10px; font-weight: bold; }
        .m-val { color: white; font-weight: 500; font-size: 13px; }
        .m-div { margin: 8px 0 !important; border-color: #334155 !important; }
        .m-input { background: #0f172a !important; color: white !important; border: 1px solid #334155 !important; }
      `}</style>
    </div>
  );
};

export default MobileScreenDetails;